import React, { useState } from "react";
import { Plus, Zap, Check, X, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatDateTime, toCents } from "@/lib/format";
import { lineStateTone } from "@/lib/wo";
import { Amount } from "@/components/common/Amount";
import { StatusChip } from "@/components/common/StatusChip";
import { Section } from "@/features/workorders/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export const LineItemsPanel = ({ wo, lineas, evidencia, canUpdate, canApprove, onDone }) => {
  const { t } = useT();
  const [adding, setAdding] = useState(false);
  const [nueva, setNueva] = useState({ descripcion: "", costo: "", precio: "" });
  const [resolving, setResolving] = useState(null); // {line, mode}
  const [costo, setCosto] = useState("");
  const [precio, setPrecio] = useState("");
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  const originales = lineas.filter((l) => l.tipo === "original");
  const adicionales = lineas.filter((l) => l.tipo === "adicional");
  const photoOf = (id) => evidencia.find((e) => e.id === id);

  const addLine = async () => {
    if (!nueva.descripcion.trim()) return;
    setBusy(true);
    try {
      await api.post(`/work-orders/${wo.id}/line-items`, {
        descripcion: nueva.descripcion.trim(),
        tipo: "original",
        sub_cost_cents: toCents(nueva.costo),
        client_price_cents: toCents(nueva.precio),
      });
      setNueva({ descripcion: "", costo: "", precio: "" });
      setAdding(false);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  const removeLine = async (id) => {
    try {
      await api.delete(`/wo-lines/${id}`);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    }
  };

  const openResolve = (line, mode) => {
    setResolving({ line, mode });
    setCosto(((line.sub_cost_cents || 0) / 100).toFixed(2));
    setPrecio(line.client_price_cents ? (line.client_price_cents / 100).toFixed(2) : "");
    setMotivo("");
  };

  const resolve = async () => {
    setBusy(true);
    try {
      if (resolving.mode === "authorize") {
        await api.post(`/wo-lines/${resolving.line.id}/authorize`, {
          sub_cost_cents: toCents(costo),
          client_price_cents: toCents(precio),
        });
      } else {
        await api.post(`/wo-lines/${resolving.line.id}/reject`, { motivo });
      }
      toast.success(t("app.saved"));
      setResolving(null);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Section
        title={t("wo.scope.title")}
        testId="wo-scope"
        action={
          canUpdate && (
            <Button variant="ghost" size="sm" data-testid="wo-scope-add" onClick={() => setAdding((v) => !v)} className="h-7 px-1">
              <Plus className="mr-1 h-3.5 w-3.5" /> {t("wo.scope.add")}
            </Button>
          )
        }
      >
        <ul>
          {originales.map((l) => (
            <li key={l.id} data-testid={`wo-scope-line-${l.id}`} className="group flex items-start gap-3 border-b border-line py-2 last:border-0">
              <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-ink-tertiary" />
              <p className="min-w-0 flex-1 text-[13px] leading-snug text-ink">{l.descripcion}</p>
              <div className="flex shrink-0 items-center gap-3">
                {l.sub_cost_cents !== undefined && <Amount cents={l.sub_cost_cents} className="text-[12.5px]" />}
                {l.client_price_cents !== undefined && <Amount cents={l.client_price_cents} className="text-[12.5px]" />}
                {canUpdate && (
                  <button
                    data-testid={`wo-scope-remove-${l.id}`}
                    onClick={() => removeLine(l.id)}
                    className="text-ink-tertiary opacity-0 transition-opacity hover:text-state-critical group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </li>
          ))}
          {!originales.length && <li className="py-4 text-[12.5px] text-ink-tertiary">{t("wo.scope.empty")}</li>}
        </ul>
        {adding && (
          <div className="mt-2 flex items-center gap-2 border-t border-line bg-surface-sunken p-3">
            <Input data-testid="wo-scope-new-desc" value={nueva.descripcion}
              onChange={(e) => setNueva({ ...nueva, descripcion: e.target.value })}
              placeholder={t("wo.form.line_ph")} className="h-8 flex-1" />
            <Input data-testid="wo-scope-new-cost" value={nueva.costo}
              onChange={(e) => setNueva({ ...nueva, costo: e.target.value })}
              placeholder={t("wo.form.line_cost")} className="h-8 w-[86px] text-right font-mono" />
            <Input data-testid="wo-scope-new-price" value={nueva.precio}
              onChange={(e) => setNueva({ ...nueva, precio: e.target.value })}
              placeholder={t("wo.form.line_price")} className="h-8 w-[86px] text-right font-mono" />
            <Button data-testid="wo-scope-new-save" disabled={busy} onClick={addLine} className="h-8">
              {t("app.save")}
            </Button>
          </div>
        )}
      </Section>

      <section className="border-l-2 border-state-warn bg-state-warn-soft px-4 py-3" data-testid="wo-additionals">
        <header className="flex items-center justify-between pb-2.5">
          <h2 className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-state-warn">
            <Zap className="h-3.5 w-3.5" />
            {t("wo.additionals.title")}
          </h2>
          {!!wo.adicionales_pendientes && (
            <StatusChip tone="amber" testId="wo-additionals-pending" label={`${wo.adicionales_pendientes} ${t("wo.additionals.pending_plural")}`} />
          )}
        </header>
        <ul>
          {adicionales.map((l) => {
            const photo = photoOf(l.evidence_id);
            return (
              <li key={l.id} data-testid={`wo-additional-${l.id}`} className="flex gap-3 border-t border-line py-3 first:border-0">
                {photo?.thumb_url ? (
                  <img src={photo.thumb_url} alt="" className="h-[74px] w-[74px] shrink-0 border border-line-strong object-cover" />
                ) : (
                  <div className="h-[74px] w-[74px] shrink-0 border border-dashed border-line-strong" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-[13px] font-medium leading-snug text-ink">{l.descripcion}</p>
                    <StatusChip tone={lineStateTone(l.estado)} label={t(`lineState.${l.estado}`)} testId={`wo-additional-state-${l.id}`} />
                  </div>
                  <p className="mt-1 text-[11.5px] text-ink-secondary">
                    {t("wo.additionals.proposed_by")} {l.propuesto_por_nombre} · {formatDateTime(l.propuesto_at || l.created_at)}
                  </p>
                  <div className="mt-1.5 flex items-center gap-4 text-[12.5px] text-ink">
                    {l.sub_cost_cents !== undefined && (
                      <span>{t("wo.cost")}: <Amount cents={l.sub_cost_cents} className="text-[12.5px]" /></span>
                    )}
                    {l.client_price_cents !== undefined && l.estado === "autorizado" && (
                      <span>{t("wo.price")}: <Amount cents={l.client_price_cents} className="text-[12.5px]" /></span>
                    )}
                  </div>
                  {l.motivo_rechazo && <p className="mt-1 text-[11.5px] italic text-state-critical">{l.motivo_rechazo}</p>}
                  {l.resuelto_por_nombre && (
                    <p className="mt-1 text-[11.5px] text-ink-secondary">
                      {t("wo.additionals.resolved_by")} {l.resuelto_por_nombre} · {formatDateTime(l.resuelto_at)}
                    </p>
                  )}
                  {l.estado === "propuesto" && canApprove && (
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" data-testid={`wo-additional-authorize-${l.id}`} onClick={() => openResolve(l, "authorize")} className="h-7 bg-state-ok px-2.5 text-[12px] text-paper hover:opacity-90">
                        <Check className="mr-1 h-3.5 w-3.5" /> {t("wo.additionals.authorize")}
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`wo-additional-reject-${l.id}`} onClick={() => openResolve(l, "reject")} className="h-7 border-state-critical px-2.5 text-[12px] text-state-critical">
                        <X className="mr-1 h-3.5 w-3.5" /> {t("wo.additionals.reject")}
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
          {!adicionales.length && <li className="py-3 text-[12.5px] text-state-warn">{t("wo.additionals.empty")}</li>}
        </ul>
      </section>

      <Dialog open={!!resolving} onOpenChange={(v) => !v && setResolving(null)}>
        <DialogContent data-testid="wo-additional-dialog" className="max-w-[480px] rounded-surface">
          <DialogHeader>
            <DialogTitle className="text-[16px]">
              {resolving?.mode === "authorize" ? t("wo.additionals.authorize_title") : t("wo.additionals.reject_title")}
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              {resolving?.mode === "authorize" ? t("wo.additionals.authorize_desc") : resolving?.line?.descripcion}
            </DialogDescription>
          </DialogHeader>

          {resolving?.mode === "authorize" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12.5px]">{t("wo.cost")}</Label>
                <Input data-testid="wo-additional-cost" value={costo} onChange={(e) => setCosto(e.target.value)}
                  className="mt-1 h-10 text-right font-mono text-[15px]" />
              </div>
              <div>
                <Label className="text-[12.5px]">{t("wo.price")} *</Label>
                <Input data-testid="wo-additional-price" value={precio} onChange={(e) => setPrecio(e.target.value)}
                  className="mt-1 h-10 text-right font-mono text-[15px]" />
              </div>
            </div>
          ) : (
            <div>
              <Label className="text-[12.5px]">{t("wo.additionals.reject_motivo")}</Label>
              <Textarea data-testid="wo-additional-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} className="mt-1" />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setResolving(null)} className="h-9">
              {t("app.cancel")}
            </Button>
            <Button data-testid="wo-additional-submit" disabled={busy} onClick={resolve} className="h-9">
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {t("app.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
